import React, { Fragment } from "react"
import { graphql, navigate } from "gatsby"
import { css } from "styled-components"
import { Slat } from "../components/template/Slat"
import breakpoint from "styled-components-breakpoint"
import { BannerDisplay } from "../components/collection/Banner"
import { BackButton } from "../components/template/elements"
import { JumbotronDisplay } from "../components/collection/Jumbotron"
import Layout from "../components/Layout"
import { H1, H3 } from "../components/atoms/Headings"
import { P } from "../components/atoms/Elements"
import { SIDEBAR_BREAKPOINT } from "../components/SideBar"
import { HR } from "../components/Typography"
import { ButtonAnchor } from "../components/atoms/Buttons"
import SEO from "../components/SEO"
import LinkResolver from "../utils/linkResolver"
import { SectionTitle } from "../components/collection/sectionTitle"

const templateCollectionPage = ({ data, pageContext, ...props }) => {
  const {
    backgroundDisplayMode,
    description,
    headerBackgroundColour,
    headerImage,
    headerTextColour,
    internal,
    relatedCollections,
    relatedCollectionsDisplayType,
    sections,
    seo,
    title,
  } = data.contentfulTemplateCollectionPage

  const seoNode = {
    title: seo.title || title,
    description: seo.description,
    pagePath: LinkResolver(internal.type, pageContext.slug),
  }

  return (
    <Layout
      hideRegionSelector
      hideDiscoverButton
      backButton={
        <BackButton
          onClick={() => {
            props.location.state?.prevPath ? navigate(-1) : navigate("/")
          }}
        >
          Go Back
        </BackButton>
      }
    >
      <SEO {...seoNode} />
      <div
        className="collectionsContainer"
        css={css`
          padding: 0;
          max-width: 1300px;

          ${breakpoint("sm")(css`
            padding: 0 20px;
          `)}

          ${breakpoint(SIDEBAR_BREAKPOINT)(
            css`
              padding: 0;
            `
          )}
        `}
      >
        <>
          <div
            css={css`
              padding: 0 24px;
              display: flex;
              align-items: center;
              border-radius: 4px;
              height: 240px;
              background-color: ${headerBackgroundColour
                ? `${headerBackgroundColour}`
                : "#6a6a6a"};

              ${backgroundDisplayMode !== "Icon" &&
              css`
                background-image: url("${headerImage?.file.url}");
                background-position: right;
                background-size: cover;
                background-repeat: no-repeat;
              `}
              color: ${headerTextColour ? `${headerTextColour}` : "white"};

              ${breakpoint("sm")(css`
                padding: 0 40px;
                background-size: contain;
              `)}
            `}
          >
            <div
              css={css`
                display: grid;
                grid-gap: 12px;
                width: 100%;

                ${breakpoint("sm")(
                  css`
                    align-items: center;
                    justify-content: space-between;
                    grid-template-columns: repeat(2, auto);
                    grid-gap: 24px;
                  `
                )}
              `}
            >
              {backgroundDisplayMode === "Icon" && (
                <img
                  src={headerImage.file.url}
                  alt=""
                  css={css`
                    height: 50px;

                    ${breakpoint("sm")(
                      css`
                        order: 1;
                        height: 100px;
                      `
                    )}

                    ${breakpoint("md")(
                      css`
                        height: 160px;
                      `
                    )}
                  `}
                />
              )}
              <div
                css={css`
                  text-align: left;
                `}
              >
                <H1
                  mb={1}
                  css={css`
                    font-size: 24px;

                    ${breakpoint("md")(
                      css`
                        font-size: 30px;
                      `
                    )}
                  `}
                >
                  {title}
                </H1>
                <P
                  css={css`
                    margin-bottom: 0;
                  `}
                >
                  {description}
                </P>
              </div>
            </div>
          </div>

          {sections?.map((section, index) => {
            let sectionTemplates

            if (section.__typename === "ContentfulCollectionCuratedSection") {
              if (section.displayType === "Jumbotrons 🐋") {
                return (
                  <Fragment key={section.contentful_id}>
                    <SectionTitle>
                      <H3>
                        {sections.length === 1 ? "Templates" : section.title}
                      </H3>
                    </SectionTitle>
                    <JumbotronDisplay jumbotrons={section.collections} />
                  </Fragment>
                )
              } else {
                return (
                  <Fragment key={section.contentful_id}>
                    <SectionTitle>
                      <H3>
                        {sections.length === 1 ? "Templates" : section.title}
                      </H3>
                    </SectionTitle>
                    <BannerDisplay templates={section.collections} />
                  </Fragment>
                )
              }
            } else {
              if (section.__typename === "ContentfulTemplateCuratedSection") {
                sectionTemplates = section.templates
              }

              if (section.__typename === "ContentfulTemplateGeneratedSection") {
                // find the templates corresponding with the section from pageContext
                const sectionTemplateIDs = Object.values(
                  pageContext.sections.find(
                    sec => Object.keys(sec)[0] === section.contentful_id
                  )
                ).flat()

                // find the templates from the graphQL query
                sectionTemplates = sectionTemplateIDs.map(sectionTemplate =>
                  data.allContentfulTemplate.nodes.find(
                    node => node.contentful_id === sectionTemplate
                  )
                )
              }
              return (
                <Fragment key={section.contentful_id}>
                  <SectionTitle>
                    <H3>
                      {sections.length === 1 ? "Templates" : section.title}
                    </H3>
                  </SectionTitle>

                  {sectionTemplates.map((template, i) => (
                    <Slat
                      first={i === 0}
                      key={template.contentfulId}
                      template={template}
                    />
                  ))}
                </Fragment>
              )
            }
          })}
          {relatedCollections && (
            <Fragment>
              <SectionTitle>
                <H3>Related Template Collections</H3>
              </SectionTitle>
              {relatedCollectionsDisplayType === "Banners 🐬" ? (
                <BannerDisplay templates={relatedCollections} />
              ) : (
                <JumbotronDisplay jumbotrons={relatedCollections} />
              )}
            </Fragment>
          )}
        </>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding-top: 32px;

            ${breakpoint("md")(
              css`
                display: none;
              `
            )}
          `}
        >
          {/* <ButtonAnchor size={2} upperCase onClick={scrollToTop}>
          Back to Top
        </ButtonAnchor> */}
          <ButtonAnchor size={2} upperCase href={"https://karbonhq.com"}>
            Discover Karbon
          </ButtonAnchor>
        </div>
        <HR
          mt={4}
          mb={1}
          css={css`
            ${breakpoint("md")(
              css`
                display: none;
              `
            )}
          `}
        />
      </div>
    </Layout>
  )
}

export const query = graphql`
  query ($slug: String!, $templateIDs: [String!]) {
    contentfulTemplateCollectionPage(slug: { eq: $slug }) {
      contentful_id
      description
      headerBackgroundColour
      headerImage {
        file {
          url
        }
      }
      headerTextColour
      internal {
        type
      }
      relatedCollectionsDisplayType
      relatedCollections {
        supertitle
        contentful_id
        item {
          contentfulId: contentful_id
          headerBackgroundColour
          headerImage {
            file {
              url
            }
          }
          headerTextColour
          internal {
            type
          }
          slug
          backgroundDisplayMode
          title
        }
      }
      sections {
        __typename
        ... on ContentfulCollectionCuratedSection {
          contentful_id
          displayType
          collections {
            item {
              contentfulId: contentful_id
              headerBackgroundColour
              headerImage {
                file {
                  url
                }
              }
              headerTextColour
              backgroundDisplayMode
              internal {
                type
              }
              id
              title
              slug
            }
            supertitle
          }
          title
        }
        ... on ContentfulTemplateCuratedSection {
          id
          contentful_id
          templates {
            ... on ContentfulTemplate {
              contentful_id
              ...ContentfulTemplateSlatFragment
            }
          }
          title
        }
        ... on ContentfulTemplateGeneratedSection {
          contentful_id
          title
        }
      }
      seo {
        description
        title
      }
      slug
      backgroundDisplayMode
      title
    }
    allContentfulTemplate(filter: { contentful_id: { in: $templateIDs } }) {
      nodes {
        contentful_id
        ...ContentfulTemplateSlatFragment
        # contentful_id
        # title
      }
    }
  }
`

export default templateCollectionPage
